import Arrow from "@assets/icons/back.svg";
import { useI18n } from "@core/i18n/i18n";
// ProgramId import removed - now using string type instead
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { getFakeProgressData, ProgressData } from "@domain/progress/progress-data";
import styled from "@emotion/styled";
import { ConditionalSkeleton, ConditionalSkeletonProps } from "@ui/components/common/conditional-skeleton";
import { ContentWithTooltip } from "@ui/components/common/content-with-tooltip";
import { ProgressCircle } from "@ui/components/common/progress-circle";
import { useProgressData } from "@ui/pages/patient-details/components/progress-tab/hooks/use-progress-data";
import {
	physioMeDesktopLargeBreakpoint,
	physioMeMobileBreakpoint,
	physioMeTabletBreakpoint,
	row,
	stack,
} from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import {
	computeDiffInDays,
	now,
	nowAsStartOfDay,
	ONE_MONTH_IN_DAYS,
	TWO_MONTH_IN_DAYS,
	TWO_WEEKS_IN_DAYS,
} from "@utils/date-utils";
import { isNumeric } from "@utils/string-utils";
import React, { useMemo } from "react";

//TODO: MAR-2849 Improve accessibility
interface ProgressDataViewProps {
	patientId: string;
	programId: string | undefined;
	programStatus?: ProgramStatus;
	isEmpty: boolean;
	isLoading: boolean;
}

export const ProgressDataView: React.FC<ProgressDataViewProps> = ({
	patientId,
	programId,
	isLoading,
	programStatus,
	...otherProps
}) => {
	const { loading, progressData } = useProgressData(patientId, programId);
	const dataAreLoading = isLoading || (progressData === undefined && loading);

	return (
		<ProgressDataContent
			patientId={patientId}
			programId={programId}
			programStatus={programStatus}
			isLoading={dataAreLoading}
			progressData={progressData ?? (dataAreLoading ? getFakeProgressData(0, 0) : getFakeProgressData())}
			{...otherProps}
		/>
	);
};

const ProgressDataContent: React.FC<
	{
		progressData: ProgressData;
	} & ProgressDataViewProps
> = ({ progressData, isEmpty, isLoading, programStatus }) => {
	const {
		format,
		formatPercent,
		formatDateRelative,
		formatProgramDuration,
		formatDurationLeft,
		formatDateMed,
		formatDateMedWithoutYear,
	} = useI18n();
	const skeletonProps = useMemo(() => ({ active: isEmpty || isLoading, loading: isLoading }), [isEmpty, isLoading]);

	const { progress, durationLeft } = useMemo(() => {
		const totalDuration = computeDiffInDays(progressData.startDate, progressData.endDate);
		const completedDuration = computeDiffInDays(progressData.startDate, nowAsStartOfDay());
		const percentage = programStatus === ProgramStatus.Ended ? 100 : (100 * completedDuration) / totalDuration;
		const leftDuration = Math.floor(computeDiffInDays(nowAsStartOfDay(), progressData.endDate));
		return { progress: percentage, durationLeft: leftDuration };
	}, [progressData.startDate, progressData.endDate, programStatus]);

	const progressCircleStrokeColor = useMemo(() => {
		if (durationLeft > TWO_MONTH_IN_DAYS) {
			return colors.primary;
		}

		if (durationLeft > TWO_WEEKS_IN_DAYS) {
			return colors.yellow;
		}

		return colors.rejection;
	}, [durationLeft]);

	const formatedRemainingTime = useMemo(() => {
		const formattedDuration = formatDurationLeft(durationLeft, TWO_MONTH_IN_DAYS);

		return (
			<>
				{formattedDuration.split(/(\d+)/).map((part, index) =>
					isNumeric(part) ? (
						<CenteredText className="p-header-2-regular" key={`duration-part-${part}-${index}`}>
							{part}
						</CenteredText>
					) : (
						part
					),
				)}
			</>
		);
	}, [durationLeft, formatDurationLeft]);

	const formatLastCompletionDate = useMemo(() => {
		if (isNil(progressData.lastCompletedSession)) {
			return format("patientDetails.progress.lastSessionCompleted.emptyValue");
		}
		if (computeDiffInDays(progressData.lastCompletedSession, now()) < ONE_MONTH_IN_DAYS) {
			return formatDateRelative(progressData.lastCompletedSession);
		} else {
			return formatDateMed(progressData.lastCompletedSession);
		}
	}, [format, formatDateMed, formatDateRelative, progressData.lastCompletedSession]);

	return (
		<SectionsContainer>
			<Section>
				<ContentWithTooltip
					tooltip={format("patientDetails.progress.programProgress.tooltip.v2_0")}
					skeleton={skeletonProps.active ? skeletonProps : undefined}
				>
					<div className="p-size-m-bold">{format("patientDetails.progress.progressSection.title")}</div>
				</ContentWithTooltip>
				<ProgressSectionContent>
					<StyledConditionalSkeleton {...skeletonProps} skeletonStyle={{ width: 200, height: 200 }}>
						<ProgressCircle
							progress={progress}
							strokeProgressColor={progressCircleStrokeColor}
							size={180}
							strokeWidth={8}
							showRemaining
							counterClockWise
						>
							{durationLeft > 0 ? (
								<CircleText className="p-size-s-rubik-regular">{formatedRemainingTime}</CircleText>
							) : (
								<CircleText className="p-size-s-rubik-regular">
									{format("patientDetails.progress.progressCircle.ended", {
										element: (
											<div>
												<CenteredText className="p-header-2-regular">
													{formatDateMedWithoutYear(progressData.endDate)}
												</CenteredText>
												<p className="p-size-s">{progressData.endDate.format("YYYY")}</p>
											</div>
										),
									})}
								</CircleText>
							)}
						</ProgressCircle>
					</StyledConditionalSkeleton>
					<Content>
						<ColumnItemContainer>
							<ConditionalSkeletonItemTitle {...skeletonProps}>
								{format("patientDetails.progress.schedule.title")}
							</ConditionalSkeletonItemTitle>
							<StyledConditionalSkeleton {...skeletonProps} skeletonStyle={{ backgroundColor: "white" }}>
								<RowContainer>
									<ConditionalSkeletonItemValue {...skeletonProps}>
										{formatDateMed(progressData.startDate)}
									</ConditionalSkeletonItemValue>
									<StyledArrow width={24} height={24} color={colors.grey} />
									<ConditionalSkeletonItemValue {...skeletonProps}>
										{formatDateMed(progressData.endDate)}
									</ConditionalSkeletonItemValue>
								</RowContainer>
							</StyledConditionalSkeleton>
							<StyledConditionalSkeleton {...skeletonProps}>
								<div className="p-size-s" style={{ color: colors.grey80 }}>
									{formatProgramDuration(progressData.startDate, progressData.endDate)}
								</div>
							</StyledConditionalSkeleton>
						</ColumnItemContainer>
						<ColumnItemContainer>
							<ConditionalSkeletonItemTitle {...skeletonProps} skeletonStyle={{ backgroundColor: "white" }}>
								{format("patientDetails.progress.lastSessionCompleted.title")}
							</ConditionalSkeletonItemTitle>
							<ConditionalSkeletonItemValue {...skeletonProps}>{formatLastCompletionDate}</ConditionalSkeletonItemValue>
						</ColumnItemContainer>
					</Content>
				</ProgressSectionContent>
			</Section>
			<Section>
				<ContentWithTooltip
					tooltip={format("patientDetails.progress.complianceSection.tooltip.v2_0")}
					skeleton={skeletonProps.active ? skeletonProps : undefined}
				>
					<div className="p-size-m-bold">{format("patientDetails.progress.complianceSection.title")}</div>
				</ContentWithTooltip>
				<ComplianceSectionContent>
					<ColumnItemContainer>
						<ConditionalSkeletonItemTitle {...skeletonProps} skeletonStyle={{ backgroundColor: "white" }}>
							{format("patientDetails.progress.compliance.title")}
						</ConditionalSkeletonItemTitle>
						<ConditionalSkeletonItemBigValue {...skeletonProps} skeletonStyle={{ borderRadius: "0.4rem" }}>
							{progressData.pastSessionsCount
								? formatPercent(progressData.programComplianceInPercent)
								: format("patientDetails.progress.programCompliance.emptyValue")}
						</ConditionalSkeletonItemBigValue>
					</ColumnItemContainer>
					<RowItemContainer>
						<ConditionalSkeletonItemTitle
							{...skeletonProps}
							skeletonStyle={{ borderRadius: "0.4rem", marginRight: 10 }}
						>
							{format("patientDetails.progress.sessionsCompleted.title")}
						</ConditionalSkeletonItemTitle>
						<ConditionalSkeletonItemValue {...skeletonProps} skeletonStyle={{ borderRadius: "0.4rem" }}>
							{format("patientDetails.progress.sessionsCompleted.value", {
								count: progressData.completedSessionsCount,
								total: progressData.pastSessionsCount,
							})}
						</ConditionalSkeletonItemValue>
					</RowItemContainer>
					<Separator />
					<RowItemContainer>
						<ConditionalSkeletonItemTitle
							{...skeletonProps}
							skeletonStyle={{ borderRadius: "0.4rem", marginRight: 10 }}
						>
							{format("patientDetails.progress.sessionsPerWeek.title")}
						</ConditionalSkeletonItemTitle>
						<ConditionalSkeletonItemValue {...skeletonProps} skeletonStyle={{ borderRadius: "0.4rem" }}>
							{progressData.sessionsPerWeek}
						</ConditionalSkeletonItemValue>
					</RowItemContainer>
					<Separator />
					<RowItemContainer>
						<ConditionalSkeletonItemTitle
							{...skeletonProps}
							skeletonStyle={{ borderRadius: "0.4rem", marginRight: 10 }}
						>
							{format("patientDetails.progress.exercisesPerWeek.title")}
						</ConditionalSkeletonItemTitle>
						<ConditionalSkeletonItemValue {...skeletonProps} skeletonStyle={{ borderRadius: "0.4rem" }}>
							{progressData.exercisesPerWeek}
						</ConditionalSkeletonItemValue>
					</RowItemContainer>
				</ComplianceSectionContent>
			</Section>
		</SectionsContainer>
	);
};

const SectionsContainer = styled.section`
	display: grid;
	grid-template-columns: 2fr 1fr;
	grid-gap: 40px;

	@media ${physioMeDesktopLargeBreakpoint} {
		grid-gap: 30px;
	}

	@media ${physioMeTabletBreakpoint} {
		grid-template-columns: 1fr;
	}
`;

const Section = styled.section`
	${stack(16, undefined, "stretch")}
`;

const ProgressSectionContent = styled.div`
	height: 100%;
	padding: 40px;
	border-radius: 8px;
	border: 1px solid ${colors.grey25};
	display: grid;
	grid-template-columns: 1fr 1fr;
	place-items: center;
	grid-gap: 40px;

	@media ${physioMeDesktopLargeBreakpoint} {
		grid-gap: 10px;
	}

	@media ${physioMeMobileBreakpoint} {
		grid-template-columns: 1fr;
		padding: 24px;
	}
`;

const ComplianceSectionContent = styled.div`
	height: 100%;
	padding: 24px;
	border-radius: 8px;
	border: 1px solid ${colors.grey25};
	${stack(12, "center")};
`;

const Separator = styled.div`
	height: 1px;
	width: 100%;
	background-color: ${colors.grey25};
`;

const RowContainer = styled.div`
	${row(8, "spacebetween", "center")};
`;

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
`;

const CircleText = styled.div`
	display: flex;
	flex-direction: column;
	text-align: center;
	white-space: nowrap;
`;

const CenteredText = styled.p`
	text-align: center;
`;

const ColumnItemContainer = styled.div`
	width: 100%;
	${stack(undefined, "flexstart", "stretch")}
`;

const StyledArrow = styled(Arrow)`
	transform: rotate(180deg);
`;

const RowItemContainer = styled.div`
	width: 100%;
	${row(undefined, "spacebetween", "center")}
`;

const StyledConditionalSkeleton = styled(ConditionalSkeleton)`
	align-self: flex-start;
	width: 100%;
`;

const ConditionalSkeletonItemTitle: React.FC<ConditionalSkeletonProps> = ({ children, ...skeletonProps }) => (
	<StyledConditionalSkeleton
		{...skeletonProps}
		skeletonStyle={{ minWidth: 50, minHeight: 20, ...skeletonProps.skeletonStyle }}
	>
		<div className="p-size-s-rubik-regular" style={{ color: colors.grey }}>
			{children}
		</div>
	</StyledConditionalSkeleton>
);
const ConditionalSkeletonItemValue: React.FC<ConditionalSkeletonProps> = ({ children, ...skeletonProps }) => (
	<StyledConditionalSkeleton {...skeletonProps} skeletonStyle={{ minWidth: 30, ...skeletonProps.skeletonStyle }}>
		<CustomDiv className="p-size-m-bold">{children}</CustomDiv>
	</StyledConditionalSkeleton>
);
const ConditionalSkeletonItemBigValue: React.FC<ConditionalSkeletonProps> = ({ children, ...skeletonProps }) => (
	<StyledConditionalSkeleton {...skeletonProps} skeletonStyle={{ minWidth: 30, ...skeletonProps.skeletonStyle }}>
		<div className="p-header-2-regular" style={{ display: "flex" }}>
			{children}
		</div>
	</StyledConditionalSkeleton>
);

const CustomDiv = styled.div`
	display: flex;
	white-space: nowrap;

	@media ${physioMeMobileBreakpoint} {
		white-space: normal;
	}
`;
